import { Button, FormField, Alert, Icon, Modal } from '@chikara/ui';
import { useResetPassword } from '@/features/auth/hooks/usePasswordReset';
import { MfaVerify } from '@/features/auth/components/MfaFlow';
import { mfaService } from '@/features/auth/services/mfaService';
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSpinner } from '@chikara/ui';
import { useUserLocation } from '@/features/auth/hooks/useLogin/useUserLocation';
import { tokenStorage } from '@/features/auth/utils/tokenStorage';
import { t } from '@/i18n';
import styles from './ResetPasswordPage.module.css';

export default function ResetPasswordPage() {
  const {
    password,
    confirmPassword,
    passwordError,
    apiError,
    isLengthValid,
    hasNumber,
    hasSpecial,
    hasUppercase,
    isFormValid,
    isTokenExpired,
    isLoading,
    handlePasswordChange,
    handleConfirmPasswordChange,
    handleSubmit,
    goToForgotPassword,
  } = useResetPassword();

  const [searchParams] = useSearchParams();
  const { show, hide, isSpinnerLoading: isGlobalLoading } = useSpinner();
  const token = searchParams.get('reference');
  const two_factor_enabled = searchParams.get('two_factor_enabled');
  
  const [isMfaVerified, setIsMfaVerified] = useState(false);
  const [mfaToken, setMfaToken] = useState('');

  const {
    locationAllowed,
    showLocationModal,
    locationError,
    errorCode,
    closeRequest,
    openRequest,
    requestLocationPermission
  } = useUserLocation();

  const getLocationErrorMessage = () => {
    if (errorCode === 1) return t('location.permissionDenied');
    if (errorCode === 2 || errorCode === 3) return t('location.positionUnavailable');
    return t('location.geoError');
  };

  useEffect(() => {
    if (token) {
      tokenStorage.setTempToken(token);
      // We set a dummy expiration to avoid getOrThrow error in getTempToken
      // assuming it's valid for now as it comes from the URL
      tokenStorage.setExpiresAt(String(Date.now() + 3600000));
    }
  }, [token]);

  useEffect(() => {
    if (two_factor_enabled === 'false') {
      setIsMfaVerified(true);
      return;
    }
    sendMfa()
    openRequest();
  }, [two_factor_enabled]);

  const sendMfa = async () => {
    show();
    try {
      const { data } = await mfaService.mfaSend({
        command: 'user-password-confirm',
        type: 'user-password-confirm'
      }, false);
      setMfaToken(data.mfa_token);
    } catch (error) {
      console.log(error);
    } finally {
      hide();
    }
  };

  const handleVerifyMfa = async (otp: string) => {
    if (!token) return;

    if (!locationAllowed) {
      openRequest();
      return;
    }

    show();

    try {
      await mfaService.mfaVerify(otp, {
        command: 'user-password-confirm',
        type: 'user-password-confirm',
        mfa_token: mfaToken,
      }, false);
      setIsMfaVerified(true);
      hide();
    } catch (error) {
      console.log(error);
      hide();
    }
  };

  if (isTokenExpired) {
    return (
      <div className={styles.expiredContainer}>
        <Icon name="error" size="lg" color="error" />
        <h2 className={styles.expiredTitle}>{t('resetPassword.tokenExpired')}</h2>
        <p className={styles.expiredSubtitle}>{t('resetPassword.tokenExpiredSubtitle')}</p>
        <Button onClick={goToForgotPassword} className={styles.submitButton}>
          {t('resetPassword.tokenExpiredAction')}
        </Button>
      </div>
    );
  }

  if (!isMfaVerified) {
    return (
      <>
        <div className={styles.mfaContainer}>
          <MfaVerify onVerify={handleVerifyMfa} isLoading={isGlobalLoading} />
        </div>

        <Modal
          style={{ visibility: isLoading ? 'hidden' : 'visible' }}
          isOpen={showLocationModal}
          title={t('location.modalTitle')}
          description={t('location.modalDescription')}
          icon={<img src="/logo_delta.svg" alt="delt.ai" width={56} height={56} />}
          actionLabel={t('location.action')}
          closeOnOverlayClick={true}
          closeOnEsc={true}
          showCloseButton={true}
          onClose={() => closeRequest()}
          onAction={requestLocationPermission}
          alert={
            locationError
              ? {
                  variant: 'error',
                  icon: <Icon name="error" color="inherit" />,
                  className: styles.alert,
                  children: getLocationErrorMessage(),
                }
              : undefined
          }
        />
      </>
    );
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <div className={styles.header}>
        <h2 className={styles.title}>{t('resetPassword.title')}</h2>
        <p className={styles.subtitle}>{t('resetPassword.subtitle')}</p>
      </div>

      <FormField
        label={t('resetPassword.password')}
        type="password"
        value={password}
        onChange={handlePasswordChange}
        required
      />

      <FormField
        label={t('resetPassword.confirmPassword')}
        type="password"
        value={confirmPassword}
        onChange={handleConfirmPasswordChange}
        error={passwordError}
        required
      />

      <div className={styles.requirementsContainer}>
        <p className={styles.requirementsTitle}>{t('resetPassword.requirements.title')}</p>
        <div className={styles.requirementsList}>
          <div>
            <Icon name={ isLengthValid ? "check" : "close"} size="sm" color={isLengthValid ? 'success' : 'error'} />
            <span>{t('resetPassword.requirements.length')}</span>
          </div>
          <div>
            <Icon name={ hasNumber ? "check" : "close"} size="sm" color={hasNumber ? 'success' : 'error'} />
            <span>{t('resetPassword.requirements.number')}</span>
          </div>
          <div>
            <Icon name={ hasSpecial ? "check" : "close"} size="sm" color={hasSpecial ? 'success' : 'error'} />
            <span>{t('resetPassword.requirements.special')}</span>
          </div>
          <div>
            <Icon name={ hasUppercase ? "check" : "close"} size="sm" color={hasUppercase ? 'success' : 'error'} />
            <span>{t('resetPassword.requirements.uppercase')}</span>
          </div>
        </div>
      </div>

      {apiError && (
        <Alert variant="error" icon={<Icon name="error" color="inherit" />}>
          {apiError}
        </Alert>
      )}

      <Button
        type="submit"
        disabled={!isFormValid}
        isLoading={isLoading}
        className={styles.submitButton}
      >
        {t('resetPassword.submit')}
      </Button>
    </form>
  );
}

export { ResetPasswordPage };
